import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { CaseWhenContainer, ConditionRow, FormulaPreview } from './styles';

interface CaseCondition {
  id: string;
  dimension: string;
  operator: string;
  value: string;
  thenValue: string;
}

interface CaseWhenValueEditorProps {
  dimensions: string[];
  onGenerate: (sql: string) => void;
}

const OPERATORS = ['=', '!=', '>', '<', '>=', '<=', 'LIKE', 'IN', 'IS NULL', 'IS NOT NULL'];

let conditionIdCounter = 0;

const createCondition = (): CaseCondition => ({
  id: `cond-${++conditionIdCounter}`,
  dimension: '',
  operator: '=',
  value: '',
  thenValue: '',
});

const CaseWhenValueEditor: React.FC<CaseWhenValueEditorProps> = ({
  dimensions,
  onGenerate,
}) => {
  const [conditions, setConditions] = useState<CaseCondition[]>([createCondition()]);
  const [elseValue, setElseValue] = useState('');

  const handleAddCondition = () => {
    setConditions((prev) => [...prev, createCondition()]);
  };

  const handleRemoveCondition = (id: string) => {
    setConditions((prev) => prev.filter((c) => c.id !== id));
  };

  const handleConditionChange = (
    id: string,
    field: keyof CaseCondition,
    value: string,
  ) => {
    setConditions((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );
  };

  const buildCaseWhenSQL = (): string => {
    const lines: string[] = ['CASE'];

    conditions.forEach((cond) => {
      if (cond.dimension && cond.thenValue) {
        const needsValue = !['IS NULL', 'IS NOT NULL'].includes(cond.operator);
        const conditionStr = needsValue
          ? `  WHEN ${cond.dimension} ${cond.operator} '${cond.value}' THEN '${cond.thenValue}'`
          : `  WHEN ${cond.dimension} ${cond.operator} THEN '${cond.thenValue}'`;
        lines.push(conditionStr);
      }
    });

    if (elseValue) {
      lines.push(`  ELSE '${elseValue}'`);
    }

    lines.push('END');
    return lines.join('\n');
  };

  const generatedSQL = buildCaseWhenSQL();

  const handleApply = () => {
    onGenerate(generatedSQL);
  };

  return (
    <CaseWhenContainer>
      <Typography variant="subtitle2" gutterBottom>
        CASE WHEN Builder
      </Typography>

      {conditions.map((cond) => (
        <ConditionRow key={cond.id}>
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 45 }}>
            WHEN
          </Typography>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Dimension</InputLabel>
            <Select
              value={cond.dimension}
              label="Dimension"
              onChange={(e: SelectChangeEvent) =>
                handleConditionChange(cond.id, 'dimension', e.target.value)
              }
            >
              {dimensions.map((dim) => (
                <MenuItem key={dim} value={dim}>
                  {dim}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Operator</InputLabel>
            <Select
              value={cond.operator}
              label="Operator"
              onChange={(e: SelectChangeEvent) =>
                handleConditionChange(cond.id, 'operator', e.target.value)
              }
            >
              {OPERATORS.map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!['IS NULL', 'IS NOT NULL'].includes(cond.operator) && (
            <TextField
              size="small"
              label="Value"
              value={cond.value}
              onChange={(e) =>
                handleConditionChange(cond.id, 'value', e.target.value)
              }
              sx={{ minWidth: 100 }}
            />
          )}

          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            THEN
          </Typography>

          <TextField
            size="small"
            label="Then"
            value={cond.thenValue}
            onChange={(e) =>
              handleConditionChange(cond.id, 'thenValue', e.target.value)
            }
            sx={{ minWidth: 100 }}
          />

          <IconButton
            size="small"
            color="error"
            onClick={() => handleRemoveCondition(cond.id)}
            disabled={conditions.length <= 1}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </ConditionRow>
      ))}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 45 }}>
          ELSE
        </Typography>
        <TextField
          size="small"
          label="Else value"
          value={elseValue}
          onChange={(e) => setElseValue(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddCondition}
          variant="outlined"
        >
          Add Condition
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleApply}
          disabled={conditions.every((c) => !c.dimension || !c.thenValue)}
        >
          Apply CASE WHEN
        </Button>
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Preview
      </Typography>
      <FormulaPreview variant="outlined">{generatedSQL}</FormulaPreview>
    </CaseWhenContainer>
  );
};

export default CaseWhenValueEditor;
